import { ReactElement, useEffect, useState } from 'react';
import {
  Chip,
  Typography,
  BreadcrumbHeading,
  Prompt,
  Export,
  EditIcon,
  DownloadIcon,
  TimerIcon,
  TickIcon,
  DuplicateIcon,
  LinkIcon,
  Bin,
  ConfigureIcon,
  Spinner,
  PrimaryButton,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import MenuSectionItems from 'src/components/MenuSectionItems';
import PublishModal from '../../components/PublishModal';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { cancelScheduledPublish } from 'src/api/cancelScheduledPublish/cancelScheduledPublish';
import { CancelScheduledPublicationPrompt } from 'src/components/CancelScheduledPublicationPrompt';
import { error } from 'src/mocks/data';
import styles from './Menu.module.scss';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { IMenu } from 'src/types/menu.types';
import { getPublicationStatus } from 'src/helpers/getPublicationStatus/getPublicationStatus';

import { Routes } from 'src/routes';
import ExportModal from 'src/components/ExportModal';
import MenuModal from 'src/components/MenuModal';
import { deleteMenu } from 'src/api/deleteMenu/deleteMenu';
import LinkVenuesModal from 'src/components/LinkVenuesModal';
import EditMenuRanges from 'src/components/EditMenuRangesModal';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

const Menu = (): ReactElement => {
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false);
  const [isCancelPromptVisible, setIsCancelPromptVisible] = useState<boolean>(false);
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [viewMoreRanges, setViewMoreRanges] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState<boolean>(false);
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState<boolean>(false);
  const [isLinkVenuesModalOpen, setIsLinkVenuesModalOpen] = useState<boolean>(false);
  const [isConfigureRangesModalOpen, setIsConfigureRangesModalOpen] = useState<boolean>(false);
  const [menuModalVariant, setMenuModalVariant] = useState<'update' | 'duplicate'>('update');
  const { menuId } = useParams<{ menuId: string }>();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentMenu, isLoading } = useMenu(menuId);
  const { data } = usePriceSchemes();
  const { trackEvent, captureException } = useUserTracking();
  const scheduledPublicationsDate = currentMenu?.scheduledPublications?.[0]?.publicationDate;
  const ranges = currentMenu?.ranges || [];
  const displayRanges = viewMoreRanges ? ranges : ranges.slice(0, 5);
  const priceSchemes = data?.map((scheme) => ({
    name: scheme.name,
    value: scheme.id,
  }));
  const publicationSubtitle = (menu?: IMenu) => {
    if (!menu) {
      return '';
    }

    const { publication, created } = menu;
    if (!publication || !created) {
      return '';
    }

    const { publicationDate, publicationStatus } = publication;
    const creationDate = formatDate(created);
    const status = getPublicationStatus(publicationStatus, publicationDate);

    return `Created: ${creationDate} | ${status}`;
  };

  const handlePublishFlow = () => {
    setIsTrial(false);
    setIsPublishModalOpen((prev: boolean) => !prev);
  };

  const handleTrialPublishFlow = () => {
    setIsTrial(true);
    setIsPublishModalOpen((prev: boolean) => !prev);
  };

  const handleCancelPrompt = () => {
    setIsCancelPromptVisible((prev: boolean) => !prev);
  };

  const handleExportModal = () => {
    setIsExportModalOpen((prev) => !prev);
  };

  const handleMenuModal = (value?: 'duplicate' | 'update') => {
    if (value) {
      setMenuModalVariant(value);
    }
    setIsMenuModalOpen((prev) => !prev);
  };

  const handleDeletePrompt = () => {
    setIsRemovePromptVisible((prev) => !prev);
  };

  const handleLinkVenuesModal = () => {
    setIsLinkVenuesModalOpen((prev) => !prev);
  };

  const handleConfigureRangesModal = () => {
    setIsConfigureRangesModalOpen((prev) => !prev);
  };

  useEffect(() => {
    document.title = currentMenu?.name || 'Openr';
    if (currentMenu?.menuId) {
      trackEvent('page_viewed', { page: 'menu_detail', menuId: currentMenu.menuId });
    }
  }, [currentMenu?.menuId]);

  const cancelPublish = async () => {
    try {
      await cancelScheduledPublish(menuId);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      handleCancelPrompt();
      trackEvent('menu_publish_cancelled', { menuId, menuName: currentMenu?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully cancelled the scheduled ${currentMenu?.name} publication.`,
      });
    } catch (e) {
      captureException(e, { tags: { action: 'cancel_publish', menuId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: error.message });
    }
  };

  const subtitle = publicationSubtitle(currentMenu);
  const handleDelete = async () => {
    try {
      await deleteMenu(menuId);
      trackEvent('menu_deleted', { menuId, menuName: currentMenu?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted "${currentMenu?.name}".`,
      });
      navigate('/');
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] });
    } catch (err) {
      captureException(err, { tags: { action: 'delete_menu', menuId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: error.message });
    }
  };

  const optionMenuItems = [
    {
      id: 'publish-trial',
      name: 'Publish to trial venues',
      onOptionClick: () => handleTrialPublishFlow(),
      icon: Export,
    },
    {
      id: 'edit-menu',
      name: 'Edit',
      onOptionClick: () => handleMenuModal('update'),
      icon: EditIcon,
    },
    {
      id: 'export-menu',
      name: 'Export Options',
      onOptionClick: handleExportModal,
      icon: DownloadIcon,
    },
    { id: 'divider-1', isDivider: true as const },
    {
      id: 'configure-ranges',
      name: 'Configure Ranges',
      onOptionClick: handleConfigureRangesModal,
      icon: ConfigureIcon,
    },
    {
      id: 'mealtimes',
      name: 'Mealtimes',
      onOptionClick: () => navigate(`${location.pathname}/mealtimes`),
      icon: TimerIcon,
    },
    {
      id: 'publish-status',
      name: 'View publish status',
      onOptionClick: () => navigate(`${location.pathname}/status`),
      icon: TickIcon,
    },
    {
      id: 'link-venues',
      name: 'Link venues',
      onOptionClick: handleLinkVenuesModal,
      icon: LinkIcon,
    },
    { id: 'divider-utils', isDivider: true as const },
    {
      id: 'duplicate-menu',
      name: 'Duplicate',
      onOptionClick: () => handleMenuModal('duplicate'),
      icon: DuplicateIcon,
    },
    {
      id: 'delete-menu',
      name: 'Delete',
      onOptionClick: handleDeletePrompt,
      hoverColour: 'tomato' as const,
      icon: Bin,
    },
  ];

  return (
    <>
      {isLoading ? (
        <SpinnerContainer data-testid="loading-spinner">
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
      ) : (
        <>
          <BreadcrumbHeading
            breadcrumbs={[
              { label: 'Menus', path: Routes.MENUS },
              { label: currentMenu?.name, path: `${Routes.MENUS}/${currentMenu?.menuId}` },
            ]}
            primaryButton={
              <PrimaryButton
                onClick={scheduledPublicationsDate ? handleCancelPrompt : handlePublishFlow}
                icon={scheduledPublicationsDate ? TimerIcon : Export}
              >
                {scheduledPublicationsDate ? 'Scheduled' : 'Publish menu'}
              </PrimaryButton>
            }
            subtitle={subtitle}
            optionMenuItems={optionMenuItems}
          />
          <div className={styles['ranges']}>
            {displayRanges.map((range) => (
              <Chip label={range.name} key={range.range_id} backgroundColour="grape" />
            ))}
            {ranges.length > 5 && (
              <div onClick={() => setViewMoreRanges(!viewMoreRanges)}>
                <Typography size="xsmall" weight="bold" className={styles['view-button']}>
                  {`View ${viewMoreRanges ? 'less' : 'more'}`}
                </Typography>
              </div>
            )}
          </div>
          <MenuSectionItems menu={currentMenu} />
          <PublishModal
            isOpen={isPublishModalOpen}
            closeModal={handlePublishFlow}
            menuId={menuId}
            menuName={currentMenu.name}
            isTrial={isTrial}
          />
          <CancelScheduledPublicationPrompt
            name={currentMenu.name}
            scheduleDate={currentMenu.scheduledPublications?.[0]?.publicationDate}
            closePrompt={handleCancelPrompt}
            open={isCancelPromptVisible}
            deleteButtonHandler={cancelPublish}
          />
          <MenuModal
            image={currentMenu?.image}
            title={`${menuModalVariant === 'update' ? 'Edit' : 'Duplicate'} menu`}
            closeModal={() => handleMenuModal()}
            open={isMenuModalOpen}
            variant={menuModalVariant}
            id={menuId}
            menuName={currentMenu?.name}
            consumerName={currentMenu?.consumerName}
          />
          <ExportModal
            title="Export Options"
            closeModal={handleExportModal}
            open={isExportModalOpen}
            id={menuId}
            name={currentMenu?.name}
            priceSchemes={priceSchemes}
            allergensPDF={currentMenu?.allergensPDF}
          />
          <EditMenuRanges
            closeModal={handleConfigureRangesModal}
            menuId={menuId}
            isOpen={isConfigureRangesModalOpen}
            currentRanges={currentMenu?.ranges}
          />
          <Prompt
            promptHeading={`Are you sure you want to delete "${currentMenu?.name}"?`}
            closePrompt={handleDeletePrompt}
            open={isRemovePromptVisible}
            buttonHandler={handleDelete}
            buttonText="Delete"
            size="small"
          >
            You will lose all the connections made with this menu.
          </Prompt>
          <LinkVenuesModal
            closeModal={handleLinkVenuesModal}
            open={isLinkVenuesModalOpen}
            menuId={menuId}
            menu={currentMenu}
            status="success"
          />
        </>
      )}
    </>
  );
};

export default Menu;
